/**
 * Phase 5.1: CommandStateManager
 *
 * This service manages state synchronization for sequential AI commands.
 * It addresses the critical bug where multiple relative size commands
 * (e.g., "narrower" → "taller" → "bigger") produce incorrect results due to:
 *
 * 1. Stale component data after updateComponent() calls
 * 2. Async React state updates being batched
 * 3. Dimensions stored in multiple locations (responsivePosition, style, size)
 * 4. Backend context timing issues
 *
 * @see Documents/ai_implementations/AI_Chat_Known_Issues.md
 */

import { Component, ResponsivePosition, Size, Position } from '@/types';
import { CSSProperties } from 'react';

/**
 * Represents a snapshot of a component's state at a specific point in time.
 * Used as the authoritative source for dimension calculations.
 */
export interface ComponentSnapshot {
  id: string;
  timestamp: number;
  style: CSSProperties;
  properties: Record<string, unknown>;
  size: ResolvedSize;
  responsivePosition: ResponsivePosition;
  // Computed values for convenience
  resolvedWidth: number;
  resolvedHeight: number;
  widthUnit: 'px' | '%' | 'auto' | 'clamp';
  heightUnit: 'px' | '%' | 'auto' | 'clamp';
}

/**
 * Size with both raw values and resolved pixel values
 */
export interface ResolvedSize {
  width: number | string;
  height: number | string;
  // Resolved pixel values for calculations
  resolvedWidthPx: number;
  resolvedHeightPx: number;
}

/**
 * Represents a pending property change waiting to be applied
 */
export interface PropertyChange {
  style?: CSSProperties;
  properties?: Record<string, unknown>;
  size?: Partial<Size>;
  responsivePosition?: Partial<ResponsivePosition>;
}

/**
 * Entry in the command queue for sequential processing
 */
interface CommandQueueEntry {
  id: string;
  command: string;
  componentId: string;
  timestamp: number;
  changes?: PropertyChange;
  status: 'pending' | 'processing' | 'applied' | 'failed';
}

/**
 * CommandStateManager - Singleton service for managing command state
 */
class CommandStateManager {
  private static instance: CommandStateManager | null = null;
  private commandQueue: Map<string, CommandQueueEntry[]> = new Map();
  private lastKnownState: Map<string, ComponentSnapshot> = new Map();
  private processingLock: Map<string, boolean> = new Map();
  private lockTimestamps: Map<string, number> = new Map();

  // Lock timeout to prevent deadlocks (10 seconds)
  private readonly LOCK_TIMEOUT_MS = 10000;

  // Container width assumption for percentage calculations (can be updated)
  private containerWidth = 1200;
  private containerHeight = 800;

  private constructor() {}

  /**
   * Get the singleton instance of CommandStateManager
   */
  static getInstance(): CommandStateManager {
    if (!this.instance) {
      this.instance = new CommandStateManager();
    }
    return this.instance;
  }

  /**
   * Set the container dimensions for percentage calculations
   */
  setContainerDimensions(width: number, height: number): void {
    this.containerWidth = width;
    this.containerHeight = height;
  }

  /**
   * Captures the current state of a component before processing a command.
   * This ensures we always have accurate baseline values.
   */
  captureState(component: Component): ComponentSnapshot {
    const resolvedSize = this.resolveCurrentSize(component);
    const allStyles = this.extractAllStyles(component);

    const snapshot: ComponentSnapshot = {
      id: component.id,
      timestamp: Date.now(),
      style: allStyles,
      properties: { ...component.properties },
      size: resolvedSize,
      responsivePosition: structuredClone(component.responsivePosition || {}),
      resolvedWidth: resolvedSize.resolvedWidthPx,
      resolvedHeight: resolvedSize.resolvedHeightPx,
      widthUnit: this.detectUnit(resolvedSize.width),
      heightUnit: this.detectUnit(resolvedSize.height),
    };

    this.lastKnownState.set(component.id, snapshot);

    return snapshot;
  }

  /**
   * Resolves the actual current size by checking all possible locations
   * in priority order.
   *
   * Priority order (highest first):
   * 1. responsivePosition.breakpoints.desktop.style (for flex containers)
   * 2. component.style
   * 3. component.size
   */

  /**
   * Type guard to check if a value is a valid CSS dimension (string or number).
   * Filters out object types that may appear in style properties.
   */
  private isValidDimension(value: unknown): value is string | number {
    return typeof value === 'string' || typeof value === 'number';
  }

  /**
   * Safely extracts a dimension value, returning undefined for invalid types.
   */
  private extractDimension(...values: unknown[]): string | number | undefined {
    for (const value of values) {
      if (this.isValidDimension(value)) {
        return value;
      }
    }
    return undefined;
  }

  private resolveCurrentSize(component: Component): ResolvedSize {
    // Priority: breakpoint style > component style > size property
    const breakpointStyle = component.responsivePosition?.breakpoints?.desktop?.style;
    const componentStyle = component.style as CSSProperties;
    const sizeProperty = component.size;

    // Get raw width value - use extractDimension to filter out invalid types
    const rawWidth = this.extractDimension(
      breakpointStyle?.width,
      componentStyle?.width,
      sizeProperty?.width
    ) ?? 'auto';

    // Get raw height value - use extractDimension to filter out invalid types
    const rawHeight = this.extractDimension(
      breakpointStyle?.height,
      componentStyle?.height,
      sizeProperty?.height
    ) ?? 'auto';

    // Resolve to pixel values
    const resolvedWidthPx = this.normalizeToPixels(rawWidth, this.containerWidth, 'width');
    const resolvedHeightPx = this.normalizeToPixels(rawHeight, this.containerHeight, 'height');

    return {
      width: rawWidth,
      height: rawHeight,
      resolvedWidthPx,
      resolvedHeightPx,
    };
  }

  /**
   * Extracts all styles from all possible locations into a single object.
   */
  private extractAllStyles(component: Component): CSSProperties {
    const breakpointStyle = component.responsivePosition?.breakpoints?.desktop?.style || {};
    const componentStyle = (component.style as CSSProperties) || {};

    // Merge with breakpoint style taking precedence
    return { ...componentStyle, ...breakpointStyle };
  }

  /**
   * Normalizes a dimension value to pixels.
   * Handles: number, 'Npx', 'N%', 'auto', 'clamp(...)'
   *
   * IMPORTANT: For 'auto' dimensions, we use sensible fallbacks based on
   * typical component sizes. This is imperfect - the actual rendered size
   * may differ. Consider setting explicit dimensions for precise control.
   */
  normalizeToPixels(value: string | number | undefined, containerSize: number, dimension: 'width' | 'height'): number {
    if (value === undefined || value === null || value === 'auto') {
      // Return sensible fallbacks for 'auto' dimensions based on typical component sizes
      // These are larger than before to avoid "taller" making things shorter
      // Width: 400px is reasonable for a medium-sized component
      // Height: 300px is typical for charts and containers
      const fallback = dimension === 'width' ? 400 : 300;
      return fallback;
    }

    if (typeof value === 'number') {
      return value;
    }

    const strValue = String(value).trim();

    // Handle pixel values: '200px', '200'
    const pxMatch = strValue.match(/^(\d+(?:\.\d+)?)\s*(?:px)?$/);
    if (pxMatch) {
      return parseFloat(pxMatch[1]);
    }

    // Handle percentage values: '75%', '50%'
    const percentMatch = strValue.match(/^(\d+(?:\.\d+)?)\s*%$/);
    if (percentMatch) {
      const percent = parseFloat(percentMatch[1]);
      return Math.round(containerSize * (percent / 100));
    }

    // Handle clamp() values: 'clamp(100px, 50%, 500px)'
    const clampMatch = strValue.match(/clamp\(\s*(\d+(?:\.\d+)?)\s*(?:px)?\s*,\s*(.+?)\s*,\s*(\d+(?:\.\d+)?)\s*(?:px)?\s*\)/i);
    if (clampMatch) {
      const minVal = parseFloat(clampMatch[1]);
      const maxVal = parseFloat(clampMatch[3]);
      // For the preferred value, try to resolve it
      const preferredStr = clampMatch[2].trim();
      let preferredVal = containerSize / 2; // Default to middle

      const preferredPercentMatch = preferredStr.match(/^(\d+(?:\.\d+)?)\s*%$/);
      if (preferredPercentMatch) {
        preferredVal = containerSize * (parseFloat(preferredPercentMatch[1]) / 100);
      } else {
        const preferredPxMatch = preferredStr.match(/^(\d+(?:\.\d+)?)\s*(?:px)?$/);
        if (preferredPxMatch) {
          preferredVal = parseFloat(preferredPxMatch[1]);
        }
      }

      return Math.min(Math.max(minVal, preferredVal), maxVal);
    }

    // Handle calc() values: 'calc(100% - 20px)'
    const calcMatch = strValue.match(/calc\(\s*(\d+(?:\.\d+)?)\s*%\s*-\s*(\d+(?:\.\d+)?)\s*(?:px)?\s*\)/i);
    if (calcMatch) {
      const percent = parseFloat(calcMatch[1]);
      const subtract = parseFloat(calcMatch[2]);
      return Math.round(containerSize * (percent / 100) - subtract);
    }

    // Fallback: try to parse as number
    const parsed = parseFloat(strValue);
    if (!isNaN(parsed)) {
      return parsed;
    }

    return dimension === 'width' ? 400 : 300;
  }

  /**
   * Detects the unit type of a dimension value.
   */
  detectUnit(value: string | number | undefined): 'px' | '%' | 'auto' | 'clamp' {
    if (value === undefined || value === null || value === 'auto') {
      return 'auto';
    }

    if (typeof value === 'number') {
      return 'px';
    }

    const strValue = String(value).trim().toLowerCase();

    if (strValue === 'auto') return 'auto';
    if (strValue.includes('clamp')) return 'clamp';
    if (strValue.includes('%')) return '%';

    return 'px';
  }

  /**
   * Acquires a processing lock for a component.
   * Returns false if another command is already processing.
   * Includes timeout-based cleanup to prevent deadlocks.
   */
  acquireLock(componentId: string): boolean {
    const currentTime = Date.now();
    const existingLockTime = this.lockTimestamps.get(componentId);

    // Check if lock exists but has timed out
    if (this.processingLock.get(componentId) && existingLockTime) {
      if (currentTime - existingLockTime > this.LOCK_TIMEOUT_MS) {
        this.releaseLock(componentId);
      }
    }

    if (this.processingLock.get(componentId)) {
      return false;
    }

    this.processingLock.set(componentId, true);
    this.lockTimestamps.set(componentId, currentTime);
    return true;
  }

  /**
   * Releases the processing lock for a component.
   */
  releaseLock(componentId: string): void {
    this.processingLock.set(componentId, false);
    this.lockTimestamps.delete(componentId);
  }

  /**
   * Checks if a component is currently locked for processing.
   */
  isLocked(componentId: string): boolean {
    return this.processingLock.get(componentId) || false;
  }

  /**
   * Gets the last known state for a component.
   * Falls back to null if no cached state exists.
   */
  getLastKnownState(componentId: string): ComponentSnapshot | null {
    return this.lastKnownState.get(componentId) || null;
  }

  /**
   * Invalidates cached state for a component.
   * Call this after external modifications (e.g., manual resize).
   */
  invalidateState(componentId: string): void {
    this.lastKnownState.delete(componentId);
  }

  /**
   * Clear all cached states (useful when switching projects)
   */
  clearAllStates(): void {
    this.lastKnownState.clear();
    this.commandQueue.clear();
    this.processingLock.clear();
    this.lockTimestamps.clear();
  }

  /**
   * Updates the cached state after changes are applied.
   * Should be called after updateComponent() and state verification.
   */
  updateCachedState(component: Component): void {
    this.captureState(component);
  }

  /**
   * Immediately updates the cached state with expected changes.
   * This is called synchronously after applying changes to ensure
   * sequential commands use the updated values.
   *
   * CRITICAL for Phase 5.1: This prevents the race condition where
   * a second command runs before the async state update completes.
   */
  updateCacheWithChanges(
    componentId: string,
    changes: {
      size?: { width?: number | string; height?: number | string };
      style?: { width?: number | string; height?: number | string };
    }
  ): void {
    const currentSnapshot = this.lastKnownState.get(componentId);
    if (!currentSnapshot) {
      return;
    }

    // Calculate new resolved dimensions from the changes
    let newWidth = currentSnapshot.resolvedWidth;
    let newHeight = currentSnapshot.resolvedHeight;
    let newRawWidth: string | number = currentSnapshot.size.width;
    let newRawHeight: string | number = currentSnapshot.size.height;

    // Apply size changes (highest priority for dimensions)
    if (changes.size?.width !== undefined) {
      newRawWidth = changes.size.width;
      newWidth = this.normalizeToPixels(changes.size.width, this.containerWidth, 'width');
    }
    if (changes.size?.height !== undefined) {
      newRawHeight = changes.size.height;
      newHeight = this.normalizeToPixels(changes.size.height, this.containerHeight, 'height');
    }

    // Apply style dimension changes (only if size wasn't set)
    if (changes.style?.width !== undefined && changes.size?.width === undefined) {
      newRawWidth = changes.style.width;
      newWidth = this.normalizeToPixels(changes.style.width, this.containerWidth, 'width');
    }
    if (changes.style?.height !== undefined && changes.size?.height === undefined) {
      newRawHeight = changes.style.height;
      newHeight = this.normalizeToPixels(changes.style.height, this.containerHeight, 'height');
    }

    // Create updated snapshot
    const updatedSnapshot: ComponentSnapshot = {
      ...currentSnapshot,
      timestamp: Date.now(),
      resolvedWidth: newWidth,
      resolvedHeight: newHeight,
      size: {
        ...currentSnapshot.size,
        width: newRawWidth,
        height: newRawHeight,
        resolvedWidthPx: newWidth,
        resolvedHeightPx: newHeight,
      },
      widthUnit: this.detectUnit(newRawWidth),
      heightUnit: this.detectUnit(newRawHeight),
    };

    this.lastKnownState.set(componentId, updatedSnapshot);
  }

  /**
   * Gets current dimensions from the snapshot for use in prompts
   */
  getCurrentDimensions(componentId: string): {
    width: number;
    height: number;
    widthUnit: 'px' | '%' | 'auto' | 'clamp';
    heightUnit: 'px' | '%' | 'auto' | 'clamp';
    rawWidth: string | number;
    rawHeight: string | number;
  } | null {
    const snapshot = this.lastKnownState.get(componentId);
    if (!snapshot) {
      return null;
    }

    return {
      width: snapshot.resolvedWidth,
      height: snapshot.resolvedHeight,
      widthUnit: snapshot.widthUnit,
      heightUnit: snapshot.heightUnit,
      rawWidth: snapshot.size.width,
      rawHeight: snapshot.size.height,
    };
  }

  /**
   * Calculates pre-computed values for relative size commands.
   * These values are included in the backend prompt to ensure accuracy.
   */
  calculateRelativeValues(componentId: string): {
    taller: number;
    shorter: number;
    wider: number;
    narrower: number;
    biggerWidth: number;
    biggerHeight: number;
    smallerWidth: number;
    smallerHeight: number;
  } | null {
    const dims = this.getCurrentDimensions(componentId);
    if (!dims) {
      return null;
    }

    // Use the same multipliers as documented in AI_Chat_Known_Issues.md
    // taller/wider = 50% increase, shorter/narrower = 25% decrease
    // bigger = 50% increase both, smaller = 25% decrease both
    return {
      taller: Math.round(dims.height * 1.5),
      shorter: Math.round(dims.height * 0.75),
      wider: Math.round(dims.width * 1.5),
      narrower: Math.round(dims.width * 0.75),
      biggerWidth: Math.round(dims.width * 1.5),
      biggerHeight: Math.round(dims.height * 1.5),
      smallerWidth: Math.round(dims.width * 0.75),
      smallerHeight: Math.round(dims.height * 0.75),
    };
  }

  /**
   * Adds a command to the queue for a component
   */
  queueCommand(componentId: string, command: string): string {
    const entry: CommandQueueEntry = {
      id: `cmd_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      command,
      componentId,
      timestamp: Date.now(),
      status: 'pending',
    };

    const queue = this.commandQueue.get(componentId) || [];
    queue.push(entry);
    this.commandQueue.set(componentId, queue);

    return entry.id;
  }

  /**
   * Gets the next pending command for a component
   */
  getNextPendingCommand(componentId: string): CommandQueueEntry | null {
    const queue = this.commandQueue.get(componentId);
    if (!queue) return null;

    return queue.find(entry => entry.status === 'pending') || null;
  }

  /**
   * Updates the status of a command in the queue
   */
  updateCommandStatus(commandId: string, status: CommandQueueEntry['status'], changes?: PropertyChange): void {
    for (const [componentId, queue] of this.commandQueue.entries()) {
      const entry = queue.find(e => e.id === commandId);
      if (entry) {
        entry.status = status;
        if (changes) {
          entry.changes = changes;
        }
        return;
      }
    }
  }

  /**
   * Cleans up completed/failed commands from the queue
   */
  cleanupQueue(componentId: string): void {
    const queue = this.commandQueue.get(componentId);
    if (!queue) return;

    const filteredQueue = queue.filter(
      entry => entry.status === 'pending' || entry.status === 'processing'
    );

    if (filteredQueue.length > 0) {
      this.commandQueue.set(componentId, filteredQueue);
    } else {
      this.commandQueue.delete(componentId);
    }
  }
}

// Export singleton instance
export const commandStateManager = CommandStateManager.getInstance();

// Also export the class for testing
export { CommandStateManager };
